import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { catchError, concatMap, finalize, forkJoin, from, last, map, merge, of, startWith, switchMap } from 'rxjs';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { Invoice } from 'src/app/models/invoice.model';
import { InvoiceLine } from 'src/app/models/invoice_line.model';
import { InvoiceRelativeToBilledPeriod } from 'src/app/models/company_product.model';
import { CompanyProductMonthlyEconomicReport } from 'src/app/models/company_product_monthly_economic_report.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';

interface ConciliationPeriodRow {
  id: number;
  billed_start_date: string;
  month_label: string;
  hours_summary_label: string;
  hours_progress: number;
  hours_progress_tooltip: string;
  billing_status_color: string;
  billing_status_label: string;
  invoice_relative_to_billed_period: InvoiceRelativeToBilledPeriod;
  is_current_month: boolean;
  is_selected: boolean;
}

interface InvoiceOtherPeriodConciliationRow {
  id: number;
  invoice_line_id: number | null;
  company_product_monthly_economic_report_id: number;
  period_label: string;
  hours_conciliated_label: string;
  amount_conciliated_label: string;
  is_automatic: boolean;
}

interface ConciliationInvoiceLineSerialized {
  id: number;
  concept: string;
  description: string | null;
  quantity: number;
  amount: number;
  conciliations_count: number;
  conciliations_tooltip: string | null;
}

interface ConciliationInvoiceSerialized {
  id: number;
  invoice_number: string;
  invoice_date: string;
  total_hours: number;
  hours_conciliated: number;
  hours_pending_conciliation: number;
  hours_pending_conciliation_label: string;
  conciliation_progress: number;
  conciliation_progress_tooltip: string;
  period_conciliations: InvoiceOtherPeriodConciliationRow[];
  other_period_conciliations: InvoiceOtherPeriodConciliationRow[];
  invoice_lines: ConciliationInvoiceLineSerialized[];
}

interface ConciliationLoadResult {
  invoices: ConciliationInvoiceSerialized[];
  periods: ConciliationPeriodRow[];
}

@Component({
  selector: 'app-company-product-monthly-economic-report-conciliation',
  templateUrl: './company-product-monthly-economic-report-conciliation.component.html',
  styleUrl: './company-product-monthly-economic-report-conciliation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CurrencyPipe,
    DatePipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatRadioModule,
    MatTooltipModule,
    MatTableModule,
    RouterLink,
    TypeSafeMatCellDeferred,
  ],
})
export class CompanyProductMonthlyEconomicReportConciliationComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly employee = inject(EmployeeService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly form_builder = inject(FormBuilder);
  private readonly snack_bar = inject(MatSnackBar);

  private readonly query_params = toSignal(this.activated_route.queryParamMap, {
    initialValue: this.activated_route.snapshot.queryParamMap,
  });
  private readonly load_trigger = signal(0);
  readonly is_loading = signal(false);

  readonly company_product_id = computed((): number | null => {
    const query_value = this.query_params().get('company_product_id');
    if (query_value) {
      const parsed = Number(query_value);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return resolveCompanyProductIdFromRouteAncestors(this.activated_route);
  });

  readonly fixed_company_product_id = computed((): number | null => resolveCompanyProductIdFromRouteAncestors(this.activated_route));

  readonly selected_period_id = signal<number | null>(null);
  readonly selected_invoice_id = signal<number | null>(null);
  readonly selected_line_ids = signal<Set<number>>(new Set());
  readonly is_saving = signal(false);

  readonly conciliation_form = this.form_builder.nonNullable.group({
    hours_conciliated: [0, [Validators.required, absoluteValueAtLeastValidator()]],
    amount_conciliated: [0, [Validators.required, absoluteValueAtLeastValidator()]],
    comment: [''],
  });

  private readonly conciliation_form_is_valid = toSignal(
    merge(this.conciliation_form.statusChanges, this.conciliation_form.valueChanges).pipe(
      startWith(null),
      map(() => this.conciliation_form.valid)
    ),
    { initialValue: this.conciliation_form.valid }
  );

  private readonly load_request = computed(() => ({
    company_product_id: this.company_product_id(),
    trigger: this.load_trigger(),
  }));

  private readonly load_result = toSignal(
    toObservable(this.load_request).pipe(
      switchMap(({ company_product_id }) => {
        if (company_product_id === null) {
          return of({ invoices: [], periods: [] } satisfies ConciliationLoadResult);
        }

        this.is_loading.set(true);
        const { billed_from, billed_to } = buildConciliationDateRange();

        return forkJoin({
          invoices: this.api_service
            .getInvoices({
              company_product_id,
              scopes: 'notFullyConciliated',
              with: 'invoice_lines,company_product_monthly_economic_report_invoice_conciliations.company_product_monthly_economic_report',
              append: 'ro_hours_conciliated',
              order_by: 'invoice_date:desc',
              per_page: 100,
            })
            .pipe(catchError(() => of({ data: [] as Invoice[] }))),
          reports: this.api_service
            .getCompanyProductMonthlyEconomicReports({
              company_product_id,
              billed_from,
              billed_to,
              with: 'company_product',
              append: COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_CONCILIATION_APPEND,
              order_by: 'billed_start_date:desc',
              per_page: 100,
            })
            .pipe(catchError(() => of({ data: [] as CompanyProductMonthlyEconomicReport[] }))),
        }).pipe(
          map(({ invoices, reports }) => ({
            invoices: invoices.data
              .filter(invoice => isInvoiceDateWithinConciliationRange(invoice.invoice_date, billed_from, billed_to))
              .map(invoice => this.serializeInvoice(invoice)),
            periods: reports.data.map(report => this.serializePeriod(report)),
          })),
          finalize(() => this.is_loading.set(false))
        );
      })
    ),
    { initialValue: { invoices: [], periods: [] } satisfies ConciliationLoadResult }
  );

  readonly invoices = computed(() => {
    const selected_period_id = this.selected_period_id();

    return this.load_result().invoices.map(invoice => ({
      ...invoice,
      other_period_conciliations: invoice.period_conciliations.filter(
        conciliation => selected_period_id === null || conciliation.company_product_monthly_economic_report_id !== selected_period_id
      ),
    }));
  });
  readonly periods = computed(() => {
    const selected_period_id = this.selected_period_id();
    return this.load_result().periods.map(period => ({
      ...period,
      is_selected: period.id === selected_period_id,
    }));
  });

  readonly can_submit = computed((): boolean => {
    return (
      this.selected_invoice_id() !== null && this.selected_period_id() !== null && this.conciliation_form_is_valid() && !this.is_saving()
    );
  });

  readonly periods_data_source = computed(() => {
    const data_source = new MatTableDataSource<ConciliationPeriodRow>();
    data_source.data = this.periods();
    return data_source;
  });

  readonly displayed_period_columns = ['select', 'month', 'hours', 'billing_status'];

  readonly list_back_link = computed((): string[] => {
    const fixed_company_product_id = this.fixed_company_product_id();
    if (fixed_company_product_id !== null) {
      return [
        '/employees',
        'development',
        'company-products',
        String(fixed_company_product_id),
        'company-product-monthly-economic-reports',
      ];
    }

    return ['/employees', 'production', 'company-product-monthly-economic-reports'];
  });

  readonly back_query_params = computed(
    (): Record<string, unknown> => removeCompanyProductIdFromQueryParams(this.activated_route.snapshot.queryParams)
  );

  readonly back_button_label = computed((): string =>
    this.fixed_company_product_id() !== null ? 'Volver a facturas y pagos' : 'Volver a facturas de productos'
  );

  toggleInvoiceLine(invoice_line_id: number, invoice_line: ConciliationInvoiceLineSerialized, checked: boolean): void {
    if (invoice_line.conciliations_count > 0) {
      return;
    }

    const next_ids = new Set(this.selected_line_ids());
    if (checked) {
      next_ids.add(invoice_line_id);
      this.autoSelectPeriodForSelectedInvoice();
    } else {
      next_ids.delete(invoice_line_id);
    }
    this.selected_line_ids.set(next_ids);
    this.recalculateFormFromSelectedLines();
  }

  isLineSelected(invoice_line_id: number): boolean {
    return this.selected_line_ids().has(invoice_line_id);
  }

  selectInvoice(invoice: ConciliationInvoiceSerialized): void {
    this.selected_invoice_id.set(invoice.id);
    this.selected_line_ids.set(new Set());
    this.conciliation_form.reset({ hours_conciliated: 0, amount_conciliated: 0, comment: '' });
  }

  selectPeriod(period: ConciliationPeriodRow): void {
    this.selected_period_id.set(period.id);
  }

  submitConciliation(): void {
    const invoice_id = this.selected_invoice_id();
    const period_id = this.selected_period_id();
    if (invoice_id === null || period_id === null || !this.conciliation_form.valid) {
      return;
    }

    const payloads = this.buildConciliationStorePayloads(invoice_id, period_id);
    if (payloads.length === 0) {
      return;
    }

    this.is_saving.set(true);
    from(payloads)
      .pipe(
        concatMap(payload => this.api_service.storeCompanyProductMonthlyEconomicReportInvoiceConciliation(payload)),
        last(),
        catchError(() => {
          this.snack_bar.open('No se pudo crear la conciliación.', '', { duration: 3000 });
          this.load_trigger.update(value => value + 1);
          return of(null);
        }),
        finalize(() => this.is_saving.set(false))
      )
      .subscribe(result => {
        if (result === null) {
          return;
        }
        this.snack_bar.open(payloads.length > 1 ? 'Conciliaciones creadas.' : 'Conciliación creada.', '', { duration: 2500 });
        this.selected_line_ids.set(new Set());
        this.selected_invoice_id.set(null);
        this.selected_period_id.set(null);
        this.conciliation_form.reset({ hours_conciliated: 0, amount_conciliated: 0, comment: '' });
        this.load_trigger.update(value => value + 1);
        this.employee.refreshActionsRequired();
      });
  }

  private serializeInvoice(invoice: Invoice): ConciliationInvoiceSerialized {
    const total_hours = this.buildInvoiceTotalHours(invoice);
    const hours_conciliated = Number(invoice.ro_hours_conciliated ?? 0);
    const hours_pending_conciliation = Number(Math.abs(total_hours - hours_conciliated).toFixed(2));
    const period_conciliations = this.serializePeriodConciliations(invoice);

    return {
      id: invoice.id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      total_hours,
      hours_conciliated,
      hours_pending_conciliation,
      hours_pending_conciliation_label: this.formatHoursLabel(hours_pending_conciliation),
      conciliation_progress: this.buildConciliationProgress(total_hours, hours_conciliated),
      conciliation_progress_tooltip: this.buildConciliationProgressTooltip(hours_pending_conciliation, total_hours),
      period_conciliations,
      other_period_conciliations: period_conciliations,
      invoice_lines: (invoice.invoice_lines ?? []).map(line =>
        this.serializeInvoiceLine(line, invoice.invoice_number, period_conciliations)
      ),
    };
  }

  private serializeInvoiceLine(
    line: InvoiceLine,
    invoice_number: string,
    period_conciliations: InvoiceOtherPeriodConciliationRow[]
  ): ConciliationInvoiceLineSerialized {
    const line_conciliations = period_conciliations.filter(conciliation => conciliation.invoice_line_id === line.id);
    const conciliations_count = line_conciliations.length;
    const line_label = this.buildInvoiceLineComment(line);

    return {
      id: line.id,
      concept: line.concept,
      description: line.description ?? null,
      quantity: Number(line.quantity),
      amount: Number(line.amount),
      conciliations_count,
      conciliations_tooltip:
        conciliations_count === 0 ? null : this.buildLineConciliationsTooltip(line_label, invoice_number, line_conciliations),
    };
  }

  private serializePeriodConciliations(invoice: Invoice): InvoiceOtherPeriodConciliationRow[] {
    return (invoice.company_product_monthly_economic_report_invoice_conciliations ?? []).map(conciliation => {
      const billed_start_date = conciliation.company_product_monthly_economic_report?.billed_start_date ?? '';
      const month_label = billed_start_date ? dayjs(billed_start_date).locale('es').format('MMMM YYYY') : 'Periodo desconocido';
      const period_label = month_label.charAt(0).toUpperCase() + month_label.slice(1);

      return {
        id: conciliation.id,
        invoice_line_id: conciliation.invoice_line_id ?? null,
        company_product_monthly_economic_report_id: conciliation.company_product_monthly_economic_report_id,
        period_label,
        hours_conciliated_label: `${Number(conciliation.hours_conciliated).toFixed(2)}h`,
        amount_conciliated_label: `${Number(conciliation.amount_conciliated).toFixed(2)} €`,
        is_automatic: conciliation.is_automatic,
      };
    });
  }

  private buildInvoiceTotalHours(invoice: Invoice): number {
    return Number((invoice.invoice_lines ?? []).reduce((total_hours, line) => total_hours + Number(line.quantity), 0).toFixed(2));
  }

  private buildConciliationProgress(total_hours: number, hours_conciliated: number): number {
    if (total_hours <= 0) {
      return 0;
    }

    return Math.min(100, Math.round((hours_conciliated / total_hours) * 10000) / 100);
  }

  private buildConciliationProgressTooltip(hours_pending_conciliation: number, total_hours: number): string {
    return `${this.formatHoursLabel(hours_pending_conciliation)} pendientes de ${this.formatHoursLabel(total_hours)}`;
  }

  private formatHoursLabel(hours: number): string {
    return `${hours.toFixed(2)}h`;
  }

  private serializePeriod(report: CompanyProductMonthlyEconomicReport): ConciliationPeriodRow {
    const month_label = dayjs(report.billed_start_date).locale('es').format('MMMM YYYY');
    const hours_registered = report.hours_registered_at_end_of_month ? Number(report.hours_registered_at_end_of_month) : 0;
    const hours_conciliated = Number(report.ro_total_hours_billed ?? 0);
    const is_current_month = dayjs(report.billed_start_date).format('YYYY-MM') === dayjs().format('YYYY-MM');

    return {
      id: report.id,
      billed_start_date: report.billed_start_date,
      month_label: month_label.charAt(0).toUpperCase() + month_label.slice(1),
      hours_summary_label: this.buildHoursSummaryLabel(hours_conciliated, hours_registered),
      hours_progress: this.buildConciliationProgress(hours_registered, hours_conciliated),
      hours_progress_tooltip: this.buildConciliatedProgressTooltip(hours_conciliated, hours_registered),
      billing_status_color: report.billing_status_color ?? '-',
      billing_status_label: this.buildBillingStatusLabel(report.billing_status_color ?? '-'),
      invoice_relative_to_billed_period: report.company_product?.invoice_relative_to_billed_period ?? 'same_month',
      is_current_month,
      is_selected: this.selected_period_id() === report.id,
    };
  }

  private buildHoursSummaryLabel(hours_conciliated: number, hours_registered: number): string {
    const conciliated_label = this.formatHoursLabel(hours_conciliated);
    const registered_label = hours_registered > 0 ? this.formatHoursLabel(hours_registered) : '—';

    return `${conciliated_label} / ${registered_label}`;
  }

  private buildConciliatedProgressTooltip(hours_conciliated: number, hours_registered: number): string {
    if (hours_registered <= 0) {
      return hours_conciliated > 0 ? `${this.formatHoursLabel(hours_conciliated)} conciliadas` : 'Sin horas conciliadas';
    }

    return `${this.formatHoursLabel(hours_conciliated)} conciliadas de ${this.formatHoursLabel(hours_registered)} registradas`;
  }

  private buildBillingStatusLabel(color: string): string {
    switch (color) {
      case 'green':
        return 'OK';
      case 'yellow':
        return 'Desviación';
      case 'red':
        return 'Fuera de rango';
      default:
        return '—';
    }
  }

  private recalculateFormFromSelectedLines(): void {
    const invoice_id = this.selected_invoice_id();
    if (invoice_id === null) {
      return;
    }
    const invoice = this.invoices().find(item => item.id === invoice_id);
    if (!invoice?.invoice_lines) {
      return;
    }

    let hours = 0;
    let amount = 0;
    for (const line of invoice.invoice_lines) {
      if (this.selected_line_ids().has(line.id)) {
        hours += Number(line.quantity);
        amount += Number(line.amount);
      }
    }

    this.conciliation_form.patchValue({
      hours_conciliated: Number(hours.toFixed(2)),
      amount_conciliated: Number(amount.toFixed(2)),
      comment: this.buildCommentFromSelectedLines(invoice.invoice_lines),
    });
  }

  private buildConciliationStorePayloads(
    invoice_id: number,
    period_id: number
  ): Array<{
    invoice_id: number;
    company_product_monthly_economic_report_id: number;
    invoice_line_id?: number | null;
    hours_conciliated: number;
    amount_conciliated: number;
    comment?: string | null;
  }> {
    const comment = this.conciliation_form.controls.comment.value.trim();
    const comment_or_null = comment !== '' ? comment : null;
    const hours_conciliated = this.conciliation_form.controls.hours_conciliated.value;
    const amount_conciliated = this.conciliation_form.controls.amount_conciliated.value;
    const invoice = this.invoices().find(item => item.id === invoice_id);
    const selected_lines = (invoice?.invoice_lines ?? []).filter(line => this.selected_line_ids().has(line.id));

    if (selected_lines.length === 0) {
      return [
        {
          invoice_id,
          company_product_monthly_economic_report_id: period_id,
          hours_conciliated,
          amount_conciliated,
          comment: comment_or_null,
        },
      ];
    }

    if (selected_lines.length === 1) {
      return [
        {
          invoice_id,
          company_product_monthly_economic_report_id: period_id,
          invoice_line_id: selected_lines[0].id,
          hours_conciliated,
          amount_conciliated,
          comment: comment_or_null,
        },
      ];
    }

    return selected_lines.map(line => {
      const line_comment = this.buildInvoiceLineComment(line);

      return {
        invoice_id,
        company_product_monthly_economic_report_id: period_id,
        invoice_line_id: line.id,
        hours_conciliated: Number(line.quantity),
        amount_conciliated: Number(line.amount),
        comment: line_comment !== '' ? line_comment : null,
      };
    });
  }

  private buildCommentFromSelectedLines(invoice_lines: ConciliationInvoiceLineSerialized[]): string {
    const selected_comments = invoice_lines
      .filter(line => this.selected_line_ids().has(line.id))
      .map(line => this.buildInvoiceLineComment(line))
      .filter(comment => comment !== '');

    return selected_comments.join(' | ');
  }

  private buildInvoiceLineComment(line: { concept: string; description?: string | null }): string {
    return buildInvoiceLineLabel(line) ?? '';
  }

  private buildLineConciliationsTooltip(
    line_label: string,
    invoice_number: string,
    conciliations: InvoiceOtherPeriodConciliationRow[]
  ): string {
    const hours_label = conciliations
      .map(conciliation => `${conciliation.hours_conciliated_label} (${conciliation.period_label})`)
      .join(', ');

    return `Línea: ${line_label !== '' ? line_label : '—'} · Factura: ${invoice_number} · Horas conciliadas: ${hours_label}`;
  }

  private autoSelectPeriodForSelectedInvoice(): void {
    const invoice_id = this.selected_invoice_id();
    if (invoice_id === null) {
      return;
    }

    const invoice = this.invoices().find(item => item.id === invoice_id);
    if (!invoice) {
      return;
    }

    const periods = this.periods();
    if (periods.length === 0) {
      return;
    }

    const invoice_relative_to_billed_period = periods[0].invoice_relative_to_billed_period;
    const target_billed_month = this.resolveBilledMonthFromInvoiceDate(invoice.invoice_date, invoice_relative_to_billed_period);

    const period_for_invoice = periods.find(period => dayjs(period.billed_start_date).format('YYYY-MM') === target_billed_month);

    if (period_for_invoice) {
      this.selected_period_id.set(period_for_invoice.id);
    }
  }

  private resolveBilledMonthFromInvoiceDate(
    invoice_date: string,
    invoice_relative_to_billed_period: InvoiceRelativeToBilledPeriod
  ): string {
    const invoice_month = dayjs(invoice_date).startOf('month');

    if (invoice_relative_to_billed_period === 'in_advance') {
      return invoice_month.add(1, 'month').format('YYYY-MM');
    }

    if (invoice_relative_to_billed_period === 'in_arrears') {
      return invoice_month.subtract(1, 'month').format('YYYY-MM');
    }

    return invoice_month.format('YYYY-MM');
  }
}

const COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_CONCILIATION_APPEND = 'ro_total_hours_billed,billing_status_color';

function absoluteValueAtLeastValidator(minimum_absolute_value = 0.01): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === '') {
      return null;
    }

    const numeric_value = Number(control.value);
    if (Number.isNaN(numeric_value)) {
      return { absoluteValueAtLeast: true };
    }

    if (Math.abs(numeric_value) < minimum_absolute_value) {
      return { absoluteValueAtLeast: true };
    }

    return null;
  };
}

export function buildInvoiceLineLabel(invoice_line: { concept: string; description?: string | null } | null | undefined): string | null {
  if (invoice_line === null || invoice_line === undefined) {
    return null;
  }

  const concept = (invoice_line.concept ?? '').trim();
  const description = (invoice_line.description ?? '').trim();

  if (concept === '' && description === '') {
    return null;
  }

  if (concept === '') {
    return description;
  }

  if (description === '') {
    return concept;
  }

  return `${concept} — ${description}`;
}

export function buildConciliationDateRange(reference_date: Date = new Date()): {
  billed_from: string;
  billed_to: string;
} {
  const current_month = dayjs(reference_date).startOf('month');

  return {
    billed_from: current_month.subtract(11, 'month').format('YYYY-MM-DD'),
    billed_to: current_month.add(1, 'month').endOf('month').format('YYYY-MM-DD'),
  };
}

export function isInvoiceDateWithinConciliationRange(invoice_date: string, billed_from: string, billed_to: string): boolean {
  const date = dayjs(invoice_date);

  return !date.isBefore(billed_from, 'day') && !date.isAfter(billed_to, 'day');
}

export function resolveCompanyProductIdFromRouteAncestors(activated_route: ActivatedRoute): number | null {
  let route: ActivatedRoute | null = activated_route;

  while (route) {
    const route_id = route.snapshot.paramMap.get('id');
    if (route_id) {
      const parsed = Number(route_id);
      return Number.isNaN(parsed) ? null : parsed;
    }
    route = route.parent;
  }

  return null;
}

function removeCompanyProductIdFromQueryParams(query_params: Record<string, unknown>): Record<string, unknown> {
  const { company_product_id: _company_product_id, ...params_without_company_product_id } = query_params;
  return params_without_company_product_id;
}
